import mapboxgl from 'mapbox-gl'

// ── Terrain 3D haute résolution IGN (RGE ALTI / LiDAR HD) ────────────────────
// Mapbox attend un DEM encodé (Terrarium / Mapbox-RGB) en tuiles Web Mercator.
// L'IGN ne sert l'altimétrie qu'en float32 brut (BIL). Mapbox GL JS n'a PAS
// d'`addProtocol` (API MapLibre) et ses custom sources ne peuvent pas être de
// type raster-dem : impossible de transformer les octets dans le thread JS.
//
// Solution : un Service Worker (public/sw-ign-dem.js) intercepte les requêtes de
// tuiles `/__ign_dem__/{z}/{x}/{y}.png`, va chercher le BIL chez l'IGN (WMS
// GetMap en EPSG:3857), décode les altitudes et ré-encode en PNG Terrarium. Côté
// Mapbox on déclare une simple source raster-dem pointant sur ce chemin local —
// tout se passe dans le navigateur, sans serveur.

const SW_URL = '/sw-ign-dem.js'
const TILE_PATH = '/__ign_dem__/{z}/{x}/{y}.png'

export const IGN_DEM_SOURCE = 'ign-dem'
export const IGN_DEM_MAXZOOM = 14 // limite RGE ALTI HIGHRES ; au-delà Mapbox sur-zoome

let swReady: Promise<void> | null = null

// Enregistre le Service Worker et résout quand il contrôle bien la page (sinon
// les premières tuiles ne seraient pas interceptées → 404).
function ensureServiceWorker (): Promise<void> {
  if (swReady) return swReady
  swReady = (async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker non supporté par ce navigateur')
    }
    await navigator.serviceWorker.register(SW_URL)
    await navigator.serviceWorker.ready
    if (!navigator.serviceWorker.controller) {
      // Premier chargement : le SW vient de s'installer, on attend qu'il prenne
      // le contrôle (il appelle clients.claim()). Garde-fou de 3 s.
      await new Promise<void>(resolve => {
        const done = () => resolve()
        navigator.serviceWorker.addEventListener('controllerchange', done, { once: true })
        setTimeout(done, 3000)
      })
    }
  })()
  return swReady
}

// Ajoute la source raster-dem IGN une fois le SW prêt (idempotent).
export async function ensureIgnDemSource (map: mapboxgl.Map): Promise<void> {
  await ensureServiceWorker()
  if (!map.getSource(IGN_DEM_SOURCE)) {
    map.addSource(IGN_DEM_SOURCE, {
      type: 'raster-dem',
      tiles: [TILE_PATH],
      tileSize: 256,
      maxzoom: IGN_DEM_MAXZOOM,
      encoding: 'terrarium',
      attribution: 'IGN — RGE ALTI / LiDAR HD (data.geopf.fr)',
    } as any)
  }
}
